import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import {
    useBranchDetailsQuery,
    useBrandListQuery,
    useCategoryAttributesQuery,
    useCategoryPickerQuery,
    useCreateProductMutation,
    useSupplierPickerQuery,
} from '@/api/queries';
import type { ProductType, ProductUnit, VatType } from '@/api/models';
import { getUser } from '@/utils';
import {
    flattenPickerCategories,
    type CategoryFilterOption,
} from '../productsMeta';
import { keepProductOrigin } from '../productOrigin';
import {
    buildCreateRequest,
    generateVariantDrafts,
    makeBatchDraft,
    makeVariantDraft,
    validateCreateForm,
    variantErrorKeys,
    type CreateProductFormState,
    type VariantDraft,
} from './createProductMeta';

/** Discriminated submit error: an i18n key (client validation) or raw server text. */
export type CreateSubmitError =
    { kind: 'i18n'; key: string } | { kind: 'raw'; message: string };

/** Editable string fields on a variant draft. */
export type VariantStringField =
    | 'barcode'
    | 'salePrice'
    | 'wholesalePrice'
    | 'minimumQuantity'
    | 'weight'
    | 'volume'
    | 'saleEndDate';

/** Editable string fields on a batch draft. */
export type BatchStringField =
    | 'batchNumber'
    | 'quantity'
    | 'purchasePrice'
    | 'receivedDate'
    | 'expiryDate';

/** A variation axis available for the chosen category, with its values. */
export type AvailableAxis = {
    attributeTypeId: string;
    name: string;
    values: { id: string; name: string }[];
};

export type SimpleOption = { id: string; name: string };

/**
 * Drives the create-product form. Owns the whole draft (identity + selected
 * variation axes + variant/batch rows), derives the picker option lists, builds
 * + submits the `POST /products` graph, and on success replaces itself in
 * history with the new product's detail page, so that page's back button skips
 * the spent form. The detail page takes over the origin the form was opened
 * from, so a later Delete returns there (bos-app#137). Cross-entity
 * validation is the server's — its 400 `error` text is surfaced inline
 * (`submitError.kind === 'raw'`).
 */
export const useCreateProductForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const createMutation = useCreateProductMutation();

    const keyRef = React.useRef(1);
    const makeKey = React.useCallback(() => `row-${keyRef.current++}`, []);

    const [state, setState] = React.useState<CreateProductFormState>(() => ({
        type: 'Simple',
        categoryId: null,
        name: '',
        sku: '',
        unit: 'Piece',
        vatType: 'Standard',
        brandId: null,
        axes: [],
        variants: [makeVariantDraft('row-0')],
    }));
    const [submitError, setSubmitError] =
        React.useState<CreateSubmitError | null>(null);

    const categoryPickerQuery = useCategoryPickerQuery();
    const brandListQuery = useBrandListQuery();
    const supplierQuery = useSupplierPickerQuery();
    const branchQuery = useBranchDetailsQuery();
    const attributesQuery = useCategoryAttributesQuery(state.categoryId);

    const categoryOptions = React.useMemo<CategoryFilterOption[]>(() => {
        const result = categoryPickerQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return flattenPickerCategories(result.response.items);
    }, [categoryPickerQuery.data]);

    const brandOptions = React.useMemo<SimpleOption[]>(() => {
        const result = brandListQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return result.response.items.map((brand) => ({
            id: brand.id,
            name: brand.name,
        }));
    }, [brandListQuery.data]);

    const supplierOptions = React.useMemo<SimpleOption[]>(() => {
        const result = supplierQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return result.response.items.map((supplier) => ({
            id: supplier.id,
            name: supplier.name,
        }));
    }, [supplierQuery.data]);

    const branchOptions = React.useMemo<SimpleOption[]>(() => {
        const result = branchQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return result.response.items.map((branch) => ({
            id: branch.id,
            name: branch.name,
        }));
    }, [branchQuery.data]);

    /**
     * Which branch a fresh batch row starts on: the signed-in user's own
     * branch, or the org's main branch for an admin who has none. Guessing is
     * safe here in a way it was not on the server — the user sees the choice
     * and can change it before submitting — and it saves the common case a
     * decision it has no reason to make.
     */
    const defaultBranchId = React.useMemo<string | null>(() => {
        const ownBranchId = getUser()?.branchId ?? null;
        if (ownBranchId) {
            return ownBranchId;
        }
        const result = branchQuery.data;
        if (!result || !result.success) {
            return null;
        }
        const items = result.response.items;
        return (
            items.find((branch) => branch.isMain)?.id ?? items[0]?.id ?? null
        );
    }, [branchQuery.data]);

    // A row added before the branch list arrived has no branch yet. Fill it in
    // once the default is known rather than leaving the user to notice a
    // blocked submit on a field the form could have answered itself.
    React.useEffect(() => {
        if (defaultBranchId === null) {
            return;
        }
        setState((prev) =>
            prev.variants.some((variant) =>
                variant.batches.some((batch) => batch.branchId === null),
            )
                ? {
                      ...prev,
                      variants: prev.variants.map((variant) => ({
                          ...variant,
                          batches: variant.batches.map((batch) =>
                              batch.branchId === null
                                  ? { ...batch, branchId: defaultBranchId }
                                  : batch,
                          ),
                      })),
                  }
                : prev,
        );
    }, [defaultBranchId]);

    /**
     * Per-variant problems, keyed by row. The bottom-of-page message names the
     * first one; this puts each on its own card, which is the only place a
     * reader can act on it (BOS-140).
     */
    const variantErrors = React.useMemo(() => variantErrorKeys(state), [state]);

    /**
     * Which variant rows the user has actually typed into, and whether they
     * have tried to submit.
     *
     * A pristine form opened at /products/new showed "Every variant needs a
     * barcode." in red before the reader had touched anything (BOS-170). It was
     * true and it was useless: an empty form is not a mistake, it is an empty
     * form, and telling someone they are wrong for not having started teaches
     * them to read past the red text — including the time it means something.
     *
     * A row shows its error once it has been edited, and every row shows it
     * once Submit has been pressed. `variantErrors` itself is untouched, so
     * `canSubmit` still knows the truth from the first render.
     */
    const [touchedVariants, setTouchedVariants] = React.useState<Set<string>>(
        () => new Set(),
    );
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);

    /** Any edit to a row — a field or an option — makes its errors worth showing. */
    const touchVariant = React.useCallback((variantKey: string) => {
        setTouchedVariants((previous) =>
            previous.has(variantKey)
                ? previous
                : new Set(previous).add(variantKey),
        );
    }, []);

    const visibleVariantErrors = React.useMemo(() => {
        if (hasAttemptedSubmit) {
            return variantErrors;
        }
        return Object.fromEntries(
            Object.entries(variantErrors).filter(([key]) =>
                touchedVariants.has(key),
            ),
        );
    }, [hasAttemptedSubmit, touchedVariants, variantErrors]);

    const availableAxes = React.useMemo<AvailableAxis[]>(() => {
        const result = attributesQuery.data;
        if (!result || !result.success) {
            return [];
        }
        return result.response.items
            .filter((item) => item.role === 'Variation')
            .map((item) => ({
                attributeTypeId: item.attributeType.id,
                name: item.attributeType.name,
                values: item.attributeType.values.map((value) => ({
                    id: value.id,
                    name: value.name,
                })),
            }));
    }, [attributesQuery.data]);

    const onTypeChange = React.useCallback(
        (type: ProductType) => {
            setState((prev) => {
                if (type === prev.type) {
                    return prev;
                }
                if (type === 'Simple') {
                    return {
                        ...prev,
                        type,
                        axes: [],
                        variants: [makeVariantDraft(makeKey())],
                    };
                }
                // Switching to Variable — clear the simple variant; the user
                // picks axes and generates the matrix.
                return { ...prev, type, axes: [], variants: [] };
            });
        },
        [makeKey],
    );

    const onCategoryChange = React.useCallback((categoryId: string | null) => {
        // The category determines which variation axes exist, so reset axes +
        // generated variants when it changes.
        setState((prev) => ({
            ...prev,
            categoryId,
            axes: [],
            variants: prev.type === 'Simple' ? prev.variants : [],
        }));
    }, []);

    const onNameChange = React.useCallback((name: string) => {
        setState((prev) => ({ ...prev, name }));
    }, []);

    const onSkuChange = React.useCallback((sku: string) => {
        setState((prev) => ({ ...prev, sku }));
    }, []);

    const onUnitChange = React.useCallback((unit: ProductUnit) => {
        setState((prev) => ({ ...prev, unit }));
    }, []);

    const onVatChange = React.useCallback((vatType: VatType) => {
        setState((prev) => ({ ...prev, vatType }));
    }, []);

    const onBrandChange = React.useCallback((brandId: string | null) => {
        setState((prev) => ({ ...prev, brandId }));
    }, []);

    const onToggleAxis = React.useCallback((attributeTypeId: string) => {
        setState((prev) => {
            const exists = prev.axes.some(
                (axis) => axis.attributeTypeId === attributeTypeId,
            );
            const axes = exists
                ? prev.axes.filter(
                      (axis) => axis.attributeTypeId !== attributeTypeId,
                  )
                : [...prev.axes, { attributeTypeId, valueIds: [] }];
            return { ...prev, axes };
        });
    }, []);

    const onToggleAxisValue = React.useCallback(
        (attributeTypeId: string, valueId: string) => {
            setState((prev) => ({
                ...prev,
                axes: prev.axes.map((axis) => {
                    if (axis.attributeTypeId !== attributeTypeId) {
                        return axis;
                    }
                    const selected = axis.valueIds.includes(valueId);
                    return {
                        ...axis,
                        valueIds: selected
                            ? axis.valueIds.filter((id) => id !== valueId)
                            : [...axis.valueIds, valueId],
                    };
                }),
            }));
        },
        [],
    );

    const onGenerateMatrix = React.useCallback(() => {
        setState((prev) => ({
            ...prev,
            variants: generateVariantDrafts(prev.axes, makeKey),
        }));
    }, [makeKey]);

    const onAddVariant = React.useCallback(() => {
        // Simple products are exactly one variant — never append a second row.
        setState((prev) =>
            prev.type === 'Simple'
                ? prev
                : {
                      ...prev,
                      variants: [...prev.variants, makeVariantDraft(makeKey())],
                  },
        );
    }, [makeKey]);

    const onRemoveVariant = React.useCallback((variantKey: string) => {
        setState((prev) => ({
            ...prev,
            variants: prev.variants.filter(
                (variant) => variant.key !== variantKey,
            ),
        }));
    }, []);

    const updateVariant = React.useCallback(
        (
            variantKey: string,
            update: (variant: VariantDraft) => VariantDraft,
        ) => {
            setState((prev) => ({
                ...prev,
                variants: prev.variants.map((variant) =>
                    variant.key === variantKey ? update(variant) : variant,
                ),
            }));
        },
        [],
    );

    const onVariantFieldChange = React.useCallback(
        (variantKey: string, field: VariantStringField, value: string) => {
            touchVariant(variantKey);
            updateVariant(variantKey, (variant) => ({
                ...variant,
                [field]: value,
            }));
        },
        [touchVariant, updateVariant],
    );

    const onVariantOptionChange = React.useCallback(
        (variantKey: string, attributeTypeId: string, valueId: string) => {
            touchVariant(variantKey);
            updateVariant(variantKey, (variant) => ({
                ...variant,
                optionValues: {
                    ...variant.optionValues,
                    [attributeTypeId]: valueId,
                },
            }));
        },
        [touchVariant, updateVariant],
    );

    const onAddBatch = React.useCallback(
        (variantKey: string) => {
            updateVariant(variantKey, (variant) => ({
                ...variant,
                batches: [
                    ...variant.batches,
                    makeBatchDraft(makeKey(), defaultBranchId),
                ],
            }));
        },
        [defaultBranchId, makeKey, updateVariant],
    );

    const onRemoveBatch = React.useCallback(
        (variantKey: string, batchKey: string) => {
            updateVariant(variantKey, (variant) => ({
                ...variant,
                batches: variant.batches.filter(
                    (batch) => batch.key !== batchKey,
                ),
            }));
        },
        [updateVariant],
    );

    const onBatchFieldChange = React.useCallback(
        (
            variantKey: string,
            batchKey: string,
            field: BatchStringField,
            value: string,
        ) => {
            updateVariant(variantKey, (variant) => ({
                ...variant,
                batches: variant.batches.map((batch) =>
                    batch.key === batchKey
                        ? { ...batch, [field]: value }
                        : batch,
                ),
            }));
        },
        [updateVariant],
    );

    const onBatchBranchChange = React.useCallback(
        (variantKey: string, batchKey: string, branchId: string | null) => {
            updateVariant(variantKey, (variant) => ({
                ...variant,
                batches: variant.batches.map((batch) =>
                    batch.key === batchKey ? { ...batch, branchId } : batch,
                ),
            }));
        },
        [updateVariant],
    );

    const onBatchSupplierChange = React.useCallback(
        (variantKey: string, batchKey: string, supplierId: string | null) => {
            updateVariant(variantKey, (variant) => ({
                ...variant,
                batches: variant.batches.map((batch) =>
                    batch.key === batchKey ? { ...batch, supplierId } : batch,
                ),
            }));
        },
        [updateVariant],
    );

    const onSubmit = React.useCallback(async () => {
        // From here on every row shows what is wrong with it: the reader has
        // said they are finished, so an unfilled field is now a mistake.
        setHasAttemptedSubmit(true);
        setSubmitError(null);
        const validationKey = validateCreateForm(state);
        if (validationKey) {
            setSubmitError({ kind: 'i18n', key: validationKey });
            return;
        }
        const result = await createMutation.mutateAsync(
            buildCreateRequest(state),
        );
        if (!result.success) {
            setSubmitError({
                kind: 'raw',
                message:
                    result.error.message ??
                    'Failed to create the product. Please try again.',
            });
            return;
        }
        navigate(PATH.PRODUCTS.BIND(result.response.id), {
            replace: true,
            state: keepProductOrigin(location.state),
        });
    }, [createMutation, location.state, navigate, state]);

    return {
        state,
        submitError,
        isSubmitting: createMutation.isPending,
        categoryOptions,
        brandOptions,
        supplierOptions,
        branchOptions,
        availableAxes,
        variantErrors: visibleVariantErrors,
        /**
         * Submit is withheld only while a variant row is already showing an
         * error (BOS-144). Gating it on the whole form instead would disable
         * the button before the user has typed a name — press-and-learn is the
         * better answer for a field nobody has reached yet, and a disabled
         * button for a problem the form has already pointed at is the better
         * answer for one they have.
         */
        canSubmit: Object.keys(variantErrors).length === 0,
        onTypeChange,
        onCategoryChange,
        onNameChange,
        onSkuChange,
        onUnitChange,
        onVatChange,
        onBrandChange,
        onToggleAxis,
        onToggleAxisValue,
        onGenerateMatrix,
        onAddVariant,
        onRemoveVariant,
        onVariantFieldChange,
        onVariantOptionChange,
        onAddBatch,
        onRemoveBatch,
        onBatchFieldChange,
        onBatchSupplierChange,
        onBatchBranchChange,
        onSubmit,
    };
};
